import styles from "./Table.module.scss";
import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { getReturnsPage, Return, ReturnItem, ReturnTypes, } from "@api/apiClient";
import DataTable, { BeforeProps } from "@containers/DataTable";
import TableSelectionPanel from "@components/Table/TableSelectionPanel";
import { TableColumn, TableColumnType, TableRowType } from "@components/Table/TableTypes";
import { Button, ButtonSize, ButtonType } from "@components/Button";
import ProgressBar from "@components/ProgressBar";
import { Text, TextColor, TextSize, TextType } from '@components/Text';
import { useProfile } from "../../../contexts/ProfileContext";
import AvatarWithMarketplace from "@components/AvatarWithMarketplace";
import TrackingNumber from "@components/TrackingNumber";
import Fire from "@assets/icons/fire.svg";
import { getProgressTo, getDaysBetweenDates } from "@utils/progress";
import BeforeUnpaidTable from "./BeforeUnpaidTable";
import { getCurrentLanguage } from "@utils/lang";
import { formatSumFromObject, formatSumWithCurrency, formatNumber } from "@utils/formatValues";
import { FilterModel } from "@hooks/useFiltersSearchParams";
import WarningIcon from "@assets/icons/warning-red-12.svg";

interface UnpaidTableProps {
  selectedSum: number;
  onClickDisposal: () => void;
  onClickDeliver: () => void;
  onSelectionChange: (ids: string[], data: Return[]) => void;
  onClickResale: (row: Return) => void;
  onClickPhoneConfirm: () => void;
  onParcelClick: (parcelId: string) => void;
  pending?: boolean,
  filters: FilterModel[],
  selectedRowIds: string[],
  selectedDelivery: number;
  selectedDepartureCount: number;
  downloadingBtn: ReactElement;
}

const UnpaidTable = ({
  selectedRowIds,
  onClickDisposal,
  onClickDeliver,
  onParcelClick,
  selectedSum,
  pending,
  onSelectionChange,
  filters,
  selectedDelivery,
  selectedDepartureCount,
  downloadingBtn,
}: UnpaidTableProps) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const profile = useProfile();
  const isPhoneConfirmed = profile.account?.isPhoneConfirmed || (profile.account?.region !== "CN");
  const variableCurrency: string = profile?.account?.region === 'TR' ? '$' : '¥';

  const BeforeTableComponent = useCallback((p: BeforeProps<Return>) => {
    return (
      <BeforeUnpaidTable {...p} />
    );
  }, []);

  const columns: TableColumn<Return>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<Return>) => (
        <TrackingNumber
          externalId={row.data?.externalId}
          trackNumber={row.data?.trackNumber}
          onClick={() => onParcelClick(row.data?.id)}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t("awaitingTable.attachment"),
      accessor: (row: TableRowType<Return>) => {
        const firstItem = row.data.items?.[0];
        return (
          <>
            {firstItem ? (
              <a href={firstItem.url ?? ""} target="_blank" rel="noreferrer">
                {firstItem.name ?? ""}
              </a>
            ) : null}
            {(row.data.qualityType === "Damaged") ? (
              <div>
                <Text
                  size={TextSize.XS}
                  color={TextColor.WHITE}
                  className={styles.qualityType}
                >
                  {t("awaitingTable.qualityTypeDamaged")}
                </Text>
              </div>
            ) : null}
          </>
        );
      },
      width: "235px"
    },
    {
      header: t("awaitingTable.quantity"),
      accessor: (row: TableRowType<Return>) => row.data.items.length ? row.data.items.reduce((accumulator: number, item: ReturnItem) => accumulator + item.quantity, 0) : '',
      alignment: "left",
      type: TableColumnType.TERTIARY
    },
    {
      header: t("awaitingTable.service"),
      accessor: (row: TableRowType<Return>) => row.data.serviceName,
      width: "200px"
    },
    {
      header: t("awaitingTable.value"),
      accessor: (row: TableRowType<Return>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {formatSumFromObject(row.data?.declaredValue)}
          </Text>
      ),
      alignment: "right",
      width: "120px"
    },
    {
      header: t("awaitingTable.delivery_cost"),
      accessor: (row: TableRowType<Return>) => (
        <div
          style={{
            backgroundColor: "#f3f4f8",
            borderRadius: "12px",
            display: "inline-block",
            padding: "3px 10px",
          }}
        >
          {formatSumFromObject(row.data.deliveryPrice)}
        </div>
      ),
      type: TableColumnType.PRIMARY,
      width: "100px",
    },
    {
      header: t("awaitingTable.seller"),
      accessor: (row: TableRowType<Return>) => (
        <AvatarWithMarketplace
          name={row.data.sellerName}
          id={row.data.sellerId}
        />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t("awaitingTable.until_disposal"),
      accessor: (row: TableRowType<Return>) => {
        const restDays = getDaysBetweenDates(null, row.data.estimatedUtilizationDate);

        return (
          <ProgressBar
            colors={[
              { value: 600/7, main: "#5ec983", bg: "#ceefda", label: "#5ec983" },
              { value: 300/7, main: "#f2af4c", bg: "#fcefdb", label: "#f2af4c" },
              { value: 0, main: "#ee6373", bg: "#fce0e3", label: "#ee6373" },
            ]}
            progress={100 - getProgressTo(row.data.estimatedUtilizationDate, 7)}
            label={restDays ? `${restDays} ${t("awaitingTable.days")}` : t("awaitingTable.lastDay")}
            column
          />
        )
      }
    },
  ], [isPhoneConfirmed, currentLanguage]);

  return (
    <TableSelectionPanel
      isShown={selectedRowIds.length > 0}
      items={[
        {
          label: t("awaitingTableSelectionPanel.selected"),
          value: selectedRowIds.length,
        },
        {
          label: t("awaitingTableSelectionPanel.sum"),
          value: `${formatNumber(selectedSum)} ₽`,
        },
        {
          label: t("awaitingTableSelectionPanel.delivery"),
          value: formatSumWithCurrency(selectedDelivery, variableCurrency),
        },
      ]}
      buttons={[
        downloadingBtn,
        (<Button
          key="disposal"
          onClick={onClickDisposal}
          size={ButtonSize.S}
          icon={Fire}
          type={ButtonType.DANGER}
          title={t("awaitingTableSelectionPanel.to_recycle")}
          disabled={selectedDepartureCount > 0}
        />),
        (<Button
          key="deliver"
          onClick={onClickDeliver}
          size={ButtonSize.S}
          title={
            t('awaitingTableSelectionPanel.to_return')
            + ` ${formatSumWithCurrency(selectedDelivery, variableCurrency)}`
          }
        />),
      ]}
      footer={(selectedDepartureCount) ? (
          <>
            {selectedDepartureCount ? (
                <>
                  <div className={styles.panelFooter}>
                    <Text
                        size={TextSize.M}
                        type={TextType.BLOCK}
                        color={TextColor.ERROR_DARK}
                    >
                      <img
                          alt="Warning"
                          className={styles.warning}
                          src={WarningIcon}
                      />
                      {t('awaitingPage.await_departure1')}
                    </Text>
                  </div>
                  {selectedRowIds.length > selectedDepartureCount && (
                      <div className={styles.panelFooter}>
                        <Text
                            size={TextSize.M}
                            type={TextType.BLOCK}
                            color={TextColor.ERROR_DARK}
                        >
                          <img
                              alt="Warning"
                              className={styles.warning}
                              src={WarningIcon}
                          />
                          {t('awaitingPage.await_departure2')}
                        </Text>
                      </div>
                  )}
                </>
            ) : null}
          </>
      ) : null}
    >
      <DataTable<Return>
        Before={BeforeTableComponent}
        queryKey="returns-page"
        pending={pending}
        getData={getReturnsPage}
        columns={columns}
        selectable
        selectedIds={selectedRowIds}
        onSelectionChange={onSelectionChange}
        notFound={t("awaitingPage.not_await")}
        filters={[
          ...filters,
          { name: "ReturnTypes", value: [ReturnTypes.AwaitingPayment] },
        ]}
        rowClassName={(row) => ((row.isSelected && row.data?.isDeparture) ? styles.extraSmallParcelRow : "")}
      />
    </TableSelectionPanel>
  );
};

export default UnpaidTable;
